import { useWidgetContent } from "@app/app/context/WidgetContentContext";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import * as React from "react";

import { cn } from "../lib/utils";
import { ThemeTitle } from "./text/Title/ThemeTitle";

export enum DialogVerticalPositions {
    Top = "top",
    Middle = "middle",
    Bottom = "bottom",
}

export enum DialogHeightModes {
    Full = "full",
    Fit = "fit",
}

export enum DialogStyleModes {
    Regular = "regular",
    Error = "error",
    Warning = "warning",
}

const popoverContainerClassNames = {
    [DialogVerticalPositions.Bottom]: {
        [DialogHeightModes.Full]: "bottom-0 max-h-[calc(100%-8rem)]",
        [DialogHeightModes.Fit]: "bottom-0 max-h-[calc(100%-8rem)]",
    },
    [DialogVerticalPositions.Middle]: {
        [DialogHeightModes.Full]: "max-h-[calc(100%-8rem)] top-1/2 -translate-y-1/2",
        [DialogHeightModes.Fit]: "max-h-[calc(100%-8rem)] top-1/2 -translate-y-1/2",
    },
    [DialogVerticalPositions.Top]: {
        [DialogHeightModes.Full]: "top-0 h-full h-full max-h-[calc(100%-8rem)]",
        [DialogHeightModes.Fit]: "top-0 max-h-[calc(100%-8rem)]",
    },
};

const MAP_STYLE_MODE_CLASS_NAMES = {
    container: {
        [DialogStyleModes.Error]: "border-2 border-red-300 outline outline-red-300 outline-2 md:rounded-md text-red-300",
        [DialogStyleModes.Warning]: "border-orange-300",
        [DialogStyleModes.Regular]: "",
    },
};

const Dialog = ({ children, ...props }: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Root>) => (
    <DialogPrimitive.Root {...props}>{children}</DialogPrimitive.Root>
);

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Overlay>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Overlay ref={ref} forceMount className={cn("absolute inset-0 z-50 bg-black/40 DialogOverlay", className)} {...props} />
));

interface DialogContentProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
    verticalPosition?: DialogVerticalPositions;
    heightMode?: DialogHeightModes;
    styleMode?: DialogStyleModes;
    isCloseable?: boolean;
    scrollContent?: boolean;
}

const DialogContent = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Content>, DialogContentProps>(
    (
        {
            className,
            children,
            verticalPosition = DialogVerticalPositions.Bottom,
            heightMode = DialogHeightModes.Fit,
            styleMode = DialogStyleModes.Regular,
            isCloseable = true,
            scrollContent = true,
            ...props
        },
        ref
    ) => {
        const { widgetContentRef } = useWidgetContent();

        if (!widgetContentRef?.current) {
            return null;
        }

        return (
            <DialogPortal container={widgetContentRef?.current}>
                <DialogOverlay className="bg-black/40 sm:rounded-xl" />
                <DialogPrimitive.Content
                    ref={ref}
                    className={cn(
                        "DialogContent absolute z-50 m-2 sm:m-4 flex left-0 bg-white right-0 h-auto w-auto max-h-full md:rounded-2xl rounded-xl",
                        scrollContent && "overflow-y-auto",
                        (popoverContainerClassNames as any)[verticalPosition][heightMode]
                    )}
                    {...props}
                >
                    <div className={cn("max-h-full w-full relative flex flex-col", MAP_STYLE_MODE_CLASS_NAMES.container[styleMode])}>
                        {children}
                        {isCloseable && (
                            <DialogPrimitive.Close
                                data-testid="dialog-close-btn"
                                className="z-40 absolute right-5 top-5 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
                            >
                                <X className="h-5 w-5" />
                                <span className="sr-only">Close</span>
                            </DialogPrimitive.Close>
                        )}
                    </div>
                </DialogPrimitive.Content>
            </DialogPortal>
        );
    }
);

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
    <div className={cn("flex flex-col space-y-1.5 px-4 pt-4 text-left pr-12", className)} {...props} />
);

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
    <div className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)} {...props} />
);

const DialogTitle = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Title>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, children, ...props }, ref) => (
    <DialogPrimitive.Title
        ref={ref}
        className={cn("font-semibold font-bold text-gray-900 leading-none tracking-tight", className)}
        asChild
        {...props}
    >
        <ThemeTitle size="xxl">{children}</ThemeTitle>
    </DialogPrimitive.Title>
));

const DialogDescription = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Description>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Description ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
));

export {
    Dialog,
    DialogPortal,
    DialogOverlay,
    DialogClose,
    DialogTrigger,
    DialogContent,
    DialogHeader,
    DialogFooter,
    DialogTitle,
    DialogDescription,
};
